import React, { useEffect, useMemo, useState } from 'react';
import { useToast } from '../../../../../components/ui/toast/ToastContext';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, Coins } from 'lucide-react';
import Info from './investmentFunds/Info';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import CustomCheckbox from '../../../../../components/ui/checkboxes/CustomCheckbox';
import SortableHeader from '../../../../../components/ui/table/SortableHeader';

const formatCurrencyPlain = (value) => {
  if (value == null) return 'R$ 0,00';

  const [intPart, decPart] = Number(value).toFixed(2).replace('.', ',').split(',');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `R$ ${formattedInt},${decPart}`;
};

const formatPercentage = (value) => {
  if (value == null) return '0,0%';

  return `${Number(value).toFixed(1).replace('.', ',')}%`;
};

const normalizeSortableText = (value) => {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const parseBrazilianDate = (value) => {
  if (!value) return 0;

  const valueAsString = String(value);

  if (valueAsString.includes('/')) {
    const [day, month, year] = valueAsString.split('/');

    return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  }

  const parsedDate = new Date(valueAsString).getTime();

  return Number.isNaN(parsedDate) ? 0 : parsedDate;
};

const getSortableValue = (position, field) => {
  switch (field) {
    case 'issuer':
      return normalizeSortableText(position.issuer);

    case 'code':
      return normalizeSortableText(position.code);

    case 'maturity':
      return parseBrazilianDate(position.maturity);

    case 'rate':
      return normalizeSortableText(position.rate);

    case 'price':
      return Number(position.price) || 0;

    case 'grossBalance':
      return Number(position.grossBalance) || 0;

    default: {
      const value = position[field];

      if (typeof value === 'number') {
        return value;
      }

      return normalizeSortableText(value);
    }
  }
};

const sortFixedIncomePositions = (positions = [], sortBy, sortOrder) => {
  const direction = sortOrder === 'desc' ? -1 : 1;

  return [...positions]
    .map((position, index) => ({ position, index }))
    .sort((a, b) => {
      const valueA = getSortableValue(a.position, sortBy);
      const valueB = getSortableValue(b.position, sortBy);

      let result = 0;

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        result = valueA - valueB;
      } else {
        result = String(valueA).localeCompare(String(valueB), 'pt-BR', {
          numeric: true,
          sensitivity: 'base',
        });
      }

      if (result === 0) {
        return a.index - b.index;
      }

      return result * direction;
    })
    .map(({ position }) => position);
};

const Title = ({ desiredPercentage, investedPercentage, count }) => {
  return (
    <div className="bg-blueLight flex flex-row justify-between items-center p-4" style={{ borderRadius: '12px' }}>
      <div className="flex items-center gap-3">
        <Coins className="text-tealBase" size={20} strokeWidth={1.75} />
        <span className="text-blueDarker text-xl font-semibold">
          Renda Fixa <span className="font-normal">({count} ativos)</span>
        </span>
      </div>

      <div className="flex items-center gap-5 text-base font-semibold">
        <span>
          Desejado: <span className="font-normal text-blueDarker">{formatPercentage(desiredPercentage)}</span>
        </span>

        <span className="text-bluePale font-normal">|</span>

        <span>
          Investido: <span className="font-normal text-blueDarker">{formatPercentage(investedPercentage)}</span>
        </span>
      </div>
    </div>
  );
};

const GRID = '30px 1.5fr 1.2fr 1fr 1fr 1fr 1fr';

const FixedIncomeRow = ({ position, idx, isSelected, onToggle }) => {
  const isBlueRow = idx % 2 === 0;

  return (
    <div className={`${isBlueRow ? 'bg-blueLight' : 'bg-white'} rounded-xl`}>
      <div className="grid items-center gap-4 px-3 py-5 text-sm text-blueDarker" style={{ gridTemplateColumns: GRID }}>
        <CustomCheckbox checked={isSelected} onChange={() => onToggle(position.id)} />

        <div className="truncate font-semibold" title={position.issuer}>
          {position.issuer}
        </div>

        <div className="truncate" title={position.code}>
          {position.code}
        </div>

        <div className="truncate">{position.maturity}</div>

        <div className="truncate">{position.rate}</div>

        <div className="truncate">{formatCurrencyPlain(position.price)}</div>

        <div className="truncate font-semibold">{formatCurrencyPlain(position.grossBalance)}</div>
      </div>
    </div>
  );
};

const FixedIncomeAccordion = ({
  title,
  investedPercentage,
  positions,
  selectedIds,
  onToggle,
  onToggleAll,
  defaultOpen = true,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [sortBy, setSortBy] = useState('issuer');
  const [sortOrder, setSortOrder] = useState('asc');

  const sortedPositions = useMemo(() => {
    return sortFixedIncomePositions(positions || [], sortBy, sortOrder);
  }, [positions, sortBy, sortOrder]);

  const visibleIds = positions.map((p) => p.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someSelected = visibleIds.some((id) => selectedIds.includes(id)) && !allSelected;

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <div className="flex flex-col">
      <div
        className="flex items-center justify-between bg-tealMedium text-white font-semibold px-4 py-3 rounded-xl cursor-pointer select-none hover:bg-tealDark transition-colors shadow-sm"
        onClick={() => setIsOpen((v) => !v)}
      >
        <span className="text-base">{title}</span>

        <div className="flex items-center gap-3">
          <span className="text-base font-semibold">
            Investido: <span className="font-medium">{formatPercentage(investedPercentage)}</span>
          </span>

          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>

      <div
        className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-2 pt-1 pb-2">
            <div
              className="grid items-center gap-4 text-blueDarker text-base font-semibold px-3 py-2"
              style={{ gridTemplateColumns: GRID }}
            >
              <CustomCheckbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() => onToggleAll(visibleIds)}
              />

              <SortableHeader
                field="issuer"
                label="Emissor"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader field="code" label="Código" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />

              <SortableHeader
                field="maturity"
                label="Vencimento"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader field="rate" label="Taxa" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />

              <SortableHeader field="price" label="Preço" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />

              <SortableHeader
                field="grossBalance"
                label="Saldo Bruto (R$)"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
            </div>

            <div className="space-y-0 mt-1">
              {sortedPositions.map((position, idx) => (
                <FixedIncomeRow
                  key={position.id}
                  position={position}
                  idx={idx}
                  isSelected={selectedIds.includes(position.id)}
                  onToggle={onToggle}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FixedIncome = ({ apiClientId }) => {
  const { addToast } = useToast();

  const [data, setData] = useState({
    count: 0,
    desiredPercentage: 0,
    investedPercentage: 0,
    totalInFixedIncome: 0,
    idealInFixedIncome: 0,
    balancing: 0,
    totalInFixedIncomePercentage: 0,
    idealInFixedIncomePercentage: 0,
    groups: [],
  });

  const [selectedIds, setSelectedIds] = useState([]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleGroup = (ids) => {
    const allIn = ids.every((id) => selectedIds.includes(id));

    if (allIn) {
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const totalMatchesIdeal = data.totalInFixedIncomePercentage === data.idealInFixedIncomePercentage;

  const fetchFixedIncomes = async () => {
    await fetch(`/app/analyst/client/${apiClientId}/dashboard/fixed_incomes`)
      .then((res) => {
        return res.json();
      })
      .then((responseData) => {
        const parsedData = camelizeKeys(responseData) || {};

        setData({
          count: parsedData.fixedIncomePositionsCount,
          desiredPercentage: parsedData.idealInFixedIncomesPercentage,
          investedPercentage: parsedData.totalInFixedIncomesPercentage,
          totalInFixedIncome: parsedData.totalInFixedIncomes,
          idealInFixedIncome: parsedData.idealInFixedIncomes,
          balancing: parsedData.balancing,
          totalInFixedIncomePercentage: parsedData.totalInFixedIncomesPercentage,
          idealInFixedIncomePercentage: parsedData.idealInFixedIncomesPercentage,
          groups: parsedData.accountingGroupCodes
            ? parsedData.accountingGroupCodes.map((agc) => ({
                id: agc.name,
                title: agc.name,
                investedPercentage: agc.percentage || 0.0,
                positions: parsedData.fixedIncomePositions
                  ? parsedData.fixedIncomePositions
                      .filter((pos) => pos.accountingGroupCode === agc.name)
                      .map(({ product, amount, maturityDate }) => ({
                        id: product.id,
                        issuer: product.issuer,
                        code: product.ticker,
                        maturity: maturityDate,
                        rate: product.indexYieldRate,
                        price: product.price,
                        grossBalance: amount,
                      }))
                  : [],
              }))
            : [],
        });

        const allPositionIds =
          parsedData.fixedIncomePositions
            ?.map((position) => position.product && position.product.id)
            ?.filter(Boolean) || [];

        setSelectedIds(allPositionIds);
      })
      .catch(() => {
        addToast({
          type: 'error',
          message: 'Erro ao carregar dados de Renda Fixa',
        });
      });
  };

  useEffect(() => {
    fetchFixedIncomes();
  }, []);

  return (
    <div className="bg-white rounded-custom20 p-4 h-full flex flex-col gap-5">
      <Title
        desiredPercentage={data.desiredPercentage}
        investedPercentage={data.investedPercentage}
        count={data.count}
      />

      <div className="grid grid-cols-3 gap-4">
        <Info
          title="Total em Renda Fixas"
          value={data.totalInFixedIncome || 0}
          percentage={data.totalInFixedIncomePercentage || 0}
          circleClassName={totalMatchesIdeal ? 'bg-blueWhisper text-tealBase' : 'bg-redExtraLight2 text-redBase'}
          tooltip={'Valores calculados com base no patrimônio total excluindo a previdência.'}
        />

        <Info
          title="Ideal em Renda Fixa"
          value={data.idealInFixedIncome || 0}
          percentage={data.idealInFixedIncomePercentage || 0}
          circleClassName="bg-blueWhisper text-tealBase"
          tooltip={'Valores calculados com base no patrimônio total excluindo a previdência.'}
        />

        <Info
          title="Balanceamento"
          value={data.balancing || 0}
          icon={
            data.balancing < 0 ? (
              <ArrowDown size={32} strokeWidth={1.5} className="text-redDeep" />
            ) : (
              <ArrowUp size={32} strokeWidth={1.5} className="text-tealBase" />
            )
          }
          circleClassName="bg-blueWhisper text-tealBase"
          tooltip={'Valores calculados com base no patrimônio total excluindo a previdência.'}
        />
      </div>

      <div className="flex flex-col gap-4">
        {data.groups.map((group) => (
          <FixedIncomeAccordion
            key={group.id}
            title={group.title}
            investedPercentage={group.investedPercentage}
            positions={group.positions}
            selectedIds={selectedIds}
            onToggle={toggleOne}
            onToggleAll={toggleGroup}
          />
        ))}
      </div>
    </div>
  );
};

export default FixedIncome;
